<template>
    <div
        class="flex md:hidden pb-1 sm:flex-row sm:flex-wrap sm:items-center gap-2"
    >
        <SearchBox
            v-if="isShowSearch"
            v-model="searchValue"
            @searchValue="searchData"
            class="flex-1 border border-[#B5B5B5] rounded-md"
        />
        <div class="flex flex-wrap gap-2 sm:ml-auto">
            <slot
                name="tableButtons"
                :items="tableData"
                :fullItems="tableFullData"
                :tableButtons="tableButtons"
            ></slot>
            <slot name="tableButtonsDataCount" :items="tableData"></slot>
        </div>
    </div>
    <div
        class="block md:hidden flex items-center justify-between gap-3 w-full px-2 py-2 mt-3 bg-nightColor-600 rounded-t-xl border-t border-r border-l text-white border-[#4B4B4B]"
    >
        <div class="flex gap-4" :class="checkBoxPermission ? 'px-3' : ''">
            <th
                v-if="checkBoxPermission"
                class="hrmThemeCheckCol themeTableCheckbox"
            >
                <div class="flex items-center justify-center">
                    <input
                        id="default-checkbox"
                        type="checkbox"
                        v-model="selectAll"
                        @change="selectAllCheckboxes"
                        class="themeInputCheckbox"
                    />
                </div>
            </th>
            <p>
                {{ tableTitle }}
            </p>
        </div>

        <details class="relative sm:hidden">
            <summary
                class="list-none cursor-pointer flex items-center bg-[#252729] text-hrmThemeTextColor justify-center rounded-md w-6 h-6"
            >
                <Icon
                    icon="ph:dots-three-vertical-bold"
                    width="20"
                    height="20"
                />
            </summary>

            <div
                class="absolute flex flex-col right-0 mt-2 w-max bg-[#272727] text-[#C3C3C3] rounded-lg shadow-lg z-40"
            >
                <ColumnSelector
                    v-if="tableData.length > 0"
                    :key="updatedColSelect"
                    :columns="availableColumns"
                    :selectedColumns="selectedColumns"
                    @setValues="updateSelections"
                />
                <hr class="border border-[#4B4B4B59] text-[#4B4B4B59]" />
                <button
                    class="inline-flex gap-2 rounded-md md:bg-black/20 p-2 md:hover:bg-black/30 focus:outline-none focus-visible:ring-2 md:focus-visible:ring-white/75"
                    @click="openTableExport()"
                >
                    <Icon
                        icon="material-symbols:export-notes-rounded"
                        color=""
                        width="15"
                        height="15"
                    />
                    <p class="text-xs font-medium">Export</p>
                </button>
            </div>
        </details>
    </div>
    <div class="block md:hidden border border-[#4B4B4B] rounded-b-xl">
        <template v-if="isDataLoading || isSearching">
            <div class="w-full flex justify-between px-2 py-3 overflow-x-auto">
                <div class="w-full flex flex-col gap-2">
                    <div class="w-full" v-for="i in 5" :key="i">
                        <Shimmer width="100%" height="60px" radius="6px" />
                    </div>
                </div>
            </div>
        </template>
        <div
            class=""
            v-if="!isDataLoading && sortedColumns && sortedColumns.length == 0"
        >
            <div class="bg-nightColor-600 rounded-lg">
                <NoRecordFound
                    :message="
                        trans(
                            'lang.messages.please_select_any_columns_to_display',
                        )
                    "
                />
            </div>
        </div>
        <div
            v-else
            v-for="(row, rowIndex) in tableData"
            :key="`${row.id}-mobile`"
            class="bg-nightColor-600 border-b border-[#4B4B4B]"
        >
            <!-- Card Header -->
            <div
                class="relative flex items-center gap-3 w-full px-2 py-2"
                @click="row._expanded = !row._expanded"
            >
                <td
                    class="hrmThemeCheckCol"
                    v-if="checkBoxPermission"
                    :id="`row${rowIndex}colcheckbox`"
                    @click="handleCellClick(rowIndex, 0)"
                    @keydown="
                        handleKeyNavigation(
                            $event,
                            rowIndex,
                            focusedCell.colIndex || 0,
                        )
                    "
                >
                    <slot name="tableCheckCol" :option="row" :items="tableData">
                    </slot>
                </td>
                <div class="flex items-center gap-3">
                    <!-- Reuse first visible column as title -->
                    <div class="text-sm font-semibold text-[#D8D8D8]">
                        <template v-if="sortedColumns?.length">
                            <template v-if="cellComponents?.[sortedColumns[0]]">
                                <component
                                    @click="handleCellClick(rowIndex, colIndex)"
                                    @keydown="
                                        handleKeyNavigation(
                                            $event,
                                            rowIndex,
                                            colIndex,
                                        )
                                    "
                                    :key="hrmTableCompKey"
                                    :is="cellComponents[sortedColumns[0]]"
                                    :employeeData="row?.[sortedColumns[0]]"
                                    :permissionPreview="true"
                                    :routeName="routeName"
                                    :rowData="row"
                                    :colTitle="row?.[sortedColumns[0]]"
                                    :colData="row?.[sortedColumns[0]]"
                                    :pageName="pageName"
                                    :tableKey="sortedColumns[0]"
                                    :statusPermission="statusPermission"
                                    :slugs="slugs"
                                    :statusUpdateApi="statusUpdateApi"
                                    :viewTypeEmployeeComp="viewTypeEmployeeComp"
                                    :isVisibleEmployees="isVisibleEmployees"
                                    :employees="employees"
                                    :locationId="locationId"
                                    :departmentId="departmentId"
                                    :assetConditions="assetConditions"
                                    :assetAvailabilities="assetAvailabilities"
                                    @updateAvailabiliti="updateAvailabiliti"
                                    @refreshTable="refreshTable"
                                    @openAdd="openAdd"
                                    @openReasonAdd="openReasonAdd"
                                    @openUpdateAttendance="openUpdateAttendance"
                                    @openReportAlert="openReportAlert"
                                    @openCandidateDetails="openCandidateDetails"
                                    @openJobScheduleDetails="
                                        openJobScheduleDetails
                                    "
                                    @submitAssetCode="submitAssetCode"
                                    @updateStatus="updateStatus"
                                    :statusUpdateType="statusUpdateType"
                                    :class="[
                                        getClassColumns(
                                            selectedColumns,
                                            availableColumns,
                                        ),
                                        isActiveCell(rowIndex, colIndex)
                                            ? 'active-cell'
                                            : '',
                                    ]"
                                />
                            </template>
                            <span
                                v-else
                                v-html="row?.[sortedColumns[0]] ?? '--'"
                            ></span>
                        </template>
                    </div>
                </div>
                <span
                    class="text-gray-400 absolute right-2 bottom-2 transition-transform"
                    :class="{ 'rotate-180': row._expanded }"
                >
                    <Icon
                        icon="cuida:dropdown-outline"
                        width="24"
                        height="24"
                    />
                </span>
                <!-- Expand icon -->
            </div>
            <!-- Card Body -->
            <div
                v-if="row._expanded"
                class="border-t border-[#4B4B4B] px-2 py-2 space-y-3 text-sm"
            >
                <!-- Data rows -->
                <div
                    v-for="(key, colIndex) in sortedColumns.slice(1)"
                    :key="`${key}-mobile`"
                    :class="[
                        'grid items-start w-full ',
                        tableTitle === trans('lang.labels.list_all_employees')
                            ? 'grid-cols-[60px_1fr] gap-2'
                            : 'grid-cols-[120px_1fr] gap-4',
                    ]"
                >
                    <!-- Label -->
                    <span class="text-white font-semibold leading-snug">
                        {{ availableColumns?.[key]?.name }}:
                    </span>
                    <!-- Value -->
                    <div class="text-[#D8D8D8] overflow-hidden">
                        <template v-if="cellComponents?.[key]">
                            <component
                                :id="`row${rowIndex}col${key}`"
                                @click="handleCellClick(rowIndex, colIndex)"
                                @keydown="
                                    handleKeyNavigation(
                                        $event,
                                        rowIndex,
                                        colIndex,
                                    )
                                "
                                :key="hrmTableCompKey"
                                :is="cellComponents[key]"
                                :employeeData="row?.[key]"
                                :permissionPreview="true"
                                :routeName="routeName"
                                :rowData="row"
                                :colTitle="row?.[key]"
                                :colData="row?.[key]"
                                :pageName="pageName"
                                :tableKey="key"
                                :statusPermission="statusPermission"
                                :slugs="slugs"
                                :statusUpdateApi="statusUpdateApi"
                                :viewTypeEmployeeComp="viewTypeEmployeeComp"
                                :isVisibleEmployees="isVisibleEmployees"
                                :employees="employees"
                                :locationId="locationId"
                                :departmentId="departmentId"
                                :assetConditions="assetConditions"
                                :assetAvailabilities="assetAvailabilities"
                                @updateAvailabiliti="updateAvailabiliti"
                                @refreshTable="refreshTable"
                                @openAdd="openAdd"
                                @openReasonAdd="openReasonAdd"
                                @openUpdateAttendance="openUpdateAttendance"
                                @openReportAlert="openReportAlert"
                                @openCandidateDetails="openCandidateDetails"
                                @openJobScheduleDetails="openJobScheduleDetails"
                                @submitAssetCode="submitAssetCode"
                                @updateStatus="updateStatus"
                                :statusUpdateType="statusUpdateType"
                                :class="[
                                    getClassColumns(
                                        selectedColumns,
                                        availableColumns,
                                    ),
                                    isActiveCell(rowIndex, colIndex)
                                        ? 'active-cell'
                                        : '',
                                ]"
                            >
                                :
                            </component>
                        </template>
                        <template v-else>
                            <span v-if="row?.[key]" v-html="row[key]"></span>
                            <span v-else>--</span>
                        </template>
                    </div>
                </div>
                <!-- Actions -->
                <div
                    v-if="actionPermission"
                    class="pt-3 grid grid-cols-[150px_fr] gap-4 items-start"
                >
                    <div class="mobile-actions">
                        <slot
                            name="tableActionCol"
                            :option="row"
                            :items="tableData"
                        ></slot>
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-nightColor-600 rounded-b-xl">
            <div class="px-5 py-5">
                <div v-if="isDataLoading" class="w-full flex justify-between">
                    <div class="w-full flex justify-between gap-3">
                        <div class="flex items-center gap-2">
                            <Shimmer width="30px" height="30px" radius="6px" />
                            <Shimmer width="80px" height="34px" radius="6px" />
                        </div>
                        <div class="flex gap-2">
                            <Shimmer
                                v-for="i in 5"
                                :key="i"
                                width="30px"
                                height="30px"
                                radius="6px"
                            />
                        </div>
                    </div>
                </div>
                <FooterTable
                    v-else
                    @refreshTable="changePageNumber"
                    :pageData="pageData"
                    :pagination="pagination"
                    @pageChange="pageChange"
                />
            </div>
        </div>
    </div>

    <div class="hidden md:block">
        <div class="bg-nightColor-600 rounded-lg">
            <div id="hrmTableHeader" class="hrm_table_header_border">
                <div class="px-5 py-4 flex items-center justify-between pb-3">
                    <template v-if="isDataLoading">
                        <div class="w-full flex justify-between">
                            <div>
                                <Shimmer
                                    width="120px"
                                    height="30px"
                                    radius="6px"
                                />
                            </div>
                            <div class="flex gap-2">
                                <Shimmer
                                    width="120px"
                                    height="30px"
                                    radius="6px"
                                />
                                <Shimmer
                                    width="250px"
                                    height="30px"
                                    radius="6px"
                                />
                                <Shimmer
                                    v-for="i in 3"
                                    :key="i"
                                    width="30px"
                                    height="30px"
                                    radius="6px"
                                />
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div>
                            <h4
                                v-if="is_restore == true"
                                class="text-base font-medium text-gray-700 themePageTitle dark:text-gray-500"
                            >
                                {{ deleteTitle }}
                            </h4>
                            <h4
                                v-else
                                class="text-lg font-medium text-gray-900 themePageTitle dark:text-gray-500"
                            >
                                {{ tableTitle }}
                            </h4>
                            <p
                                class="text-sm font-medium text-gray-900 dark:text-gray-400"
                            >
                                {{ subTableTitle }}
                            </p>
                            <slot
                                name="tableExtraInfo"
                                :tableHeadInfo="tableHeadInfo"
                            ></slot>
                        </div>
                        <div
                            class="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2"
                        >
                            <ColumnSelector
                                :key="updatedColSelect"
                                v-if="tableData.length > 0"
                                :columns="availableColumns"
                                :selectedColumns="selectedColumns"
                                @setValues="updateSelections"
                            />
                            <SearchBox
                                v-if="isShowSearch"
                                v-model="searchValue"
                                @searchValue="searchData"
                                class="flex-1"
                            />
                            <div class="flex flex-wrap gap-2 sm:ml-auto">
                                <slot
                                    name="tableButtons"
                                    :items="tableData"
                                    :fullItems="tableFullData"
                                    :tableButtons="tableButtons"
                                    :tableHeadInfo="tableHeadInfo"
                                ></slot>
                                <slot
                                    name="tableButtonsDataCount"
                                    :items="tableData"
                                ></slot>

                                <hrm-button
                                    v-if="showExportButton"
                                    @click="openTableExport()"
                                    buttonMode="iconButton"
                                    iconSize="20px"
                                    buttonPadding="0"
                                    buttonSize="36px"
                                    buttonClass="flex justify-center items-center"
                                    buttonAlign="center"
                                    icon="material-symbols:export-notes-rounded"
                                    tooltipText="Export"
                                    tooltipPosition="top"
                                />
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <template v-if="isDataLoading">
                <div
                    class="w-full flex justify-between px-5 py-3 overflow-x-auto"
                >
                    <div class="w-full flex flex-col gap-2">
                        <div class="w-full" v-for="i in 5" :key="i">
                            <Shimmer width="100%" height="30px" radius="6px" />
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <template v-if="apiEndpoint">
                    <div
                        :key="hrmTableCompKey"
                        v-if="tableData && tableData.length > 0"
                        class="px-5 py-3"
                    >
                        <div
                            class=""
                            v-if="sortedColumns && sortedColumns.length == 0"
                        >
                            <div class="mt-10 bg-nightColor-600 rounded-lg">
                                <NoRecordFound
                                    :message="
                                        trans(
                                            'lang.messages.please_select_any_columns_to_display',
                                        )
                                    "
                                />
                            </div>
                        </div>
                        <div
                            ref="tableContainer"
                            v-else
                            class="overflow-x-auto hrm-table-overflow overflow-y-auto"
                            :style="{ maxHeight: tableHeight }"
                        >
                            <template v-if="isSearching">
                                <div
                                    class="w-full flex justify-between overflow-x-auto"
                                >
                                    <div class="w-full flex flex-col gap-2">
                                        <div
                                            class="w-full"
                                            v-for="i in 5"
                                            :key="i"
                                        >
                                            <Shimmer
                                                width="100%"
                                                height="30px"
                                                radius="6px"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <table
                                    class="hrmTheme_table hrm_Table w-full max-sm:w-[900px] sm:w-[990px] xl:w-full"
                                >
                                    <thead class="sticky top-0 z-20">
                                        <tr>
                                            <th
                                                v-if="checkBoxPermission"
                                                class="hrmThemeCheckCol themeTableCheckbox"
                                            >
                                                <div
                                                    class="flex items-center justify-center"
                                                >
                                                    <input
                                                        id="default-checkbox"
                                                        type="checkbox"
                                                        v-model="selectAll"
                                                        @change="
                                                            selectAllCheckboxes
                                                        "
                                                        class="themeInputCheckbox"
                                                    />
                                                </div>
                                            </th>

                                            <template
                                                v-for="(
                                                    key, colIndex
                                                ) in sortedColumns"
                                                :key="`${key}tableHeadKey`"
                                            >
                                                <template
                                                    v-if="
                                                        headComponents &&
                                                        headComponents[key]
                                                    "
                                                >
                                                    <component
                                                        :is="
                                                            headComponents[key]
                                                        "
                                                        :allMonthDate="
                                                            allMonthDate
                                                        "
                                                        :slugs="slugs"
                                                        :statusPermission="
                                                            statusPermission
                                                        "
                                                        :headTitle="
                                                            availableColumns?.[
                                                                key
                                                            ]?.name
                                                        "
                                                        :class="[
                                                            getClassColumns(
                                                                selectedColumns,
                                                                availableColumns,
                                                            ),
                                                            focusedCell.colIndex ==
                                                            colIndex
                                                                ? 'hrmTableActiveCol'
                                                                : '',
                                                        ]"
                                                    >
                                                    </component>
                                                </template>
                                                <template v-else>
                                                    <th
                                                        class="hrmThemeTableTh"
                                                        :class="[
                                                            getClassColumns(
                                                                selectedColumns,
                                                                availableColumns,
                                                            ),
                                                            focusedCell.colIndex ==
                                                            colIndex
                                                                ? 'hrmTableActiveCol'
                                                                : '',
                                                            stickyCols &&
                                                            stickyCols.includes(
                                                                key,
                                                            )
                                                                ? 'sticky left-0 z-20'
                                                                : '',
                                                        ]"
                                                    >
                                                        <p
                                                            class="flex items-center"
                                                        >
                                                            {{
                                                                availableColumns?.[
                                                                    key
                                                                ]?.name
                                                            }}

                                                            <ToggleTooltip
                                                                v-if="
                                                                    availableColumns?.[
                                                                        key
                                                                    ]?.info
                                                                "
                                                                :details="
                                                                    availableColumns?.[
                                                                        key
                                                                    ]?.info
                                                                "
                                                            />
                                                        </p>
                                                    </th>
                                                </template>
                                            </template>
                                            <th
                                                v-if="actionPermission"
                                                class="hrmThemeActionCol themeActionTh"
                                            >
                                                {{
                                                    trans("lang.labels.action")
                                                }}
                                            </th>
                                            <slot
                                                name="actionMultiPermissions"
                                            ></slot>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(row, rowIndex) in tableData"
                                            :key="`${row.id}tableRowKey`"
                                                                :class="[
                                                            isActiveRow(row),
                                                            (focusedCell.rowIndex === rowIndex &&
                                                            !(row.basic_salary == 0 && salary_flag))
                                                            ? 'hrmTableActiveRow'
                                                            : (row.basic_salary == 0 && salary_flag)
                                                                ? '!bg-red-800/40'
                                                                : ''
                                                            ]"
                                        >
                                        
                                            <td
                                                class="hrmThemeCheckCol"
                                                v-if="checkBoxPermission"
                                                :id="`row${rowIndex}colcheckbox`"
                                                @click="
                                                    handleCellClick(rowIndex, 0)
                                                "
                                                @keydown="
                                                    handleKeyNavigation(
                                                        $event,
                                                        rowIndex,
                                                        focusedCell.colIndex ||
                                                            0,
                                                    )
                                                "
                                            >
                                                <slot
                                                    name="tableCheckCol"
                                                    :option="row"
                                                    :items="tableData"
                                                >
                                                </slot>
                                            </td>
                                            <template
                                                v-for="(
                                                    key, colIndex
                                                ) in sortedColumns"
                                                :key="`${key}columnKey`"
                                            >
                                                <template
                                                    v-if="
                                                        cellComponents &&
                                                        cellComponents[key]
                                                    "
                                                >
                                                    <component
                                                        :id="`row${rowIndex}col${key}`"
                                                        @click="
                                                            handleCellClick(
                                                                rowIndex,
                                                                colIndex,
                                                            )
                                                        "
                                                        @keydown="
                                                            handleKeyNavigation(
                                                                $event,
                                                                rowIndex,
                                                                colIndex,
                                                            )
                                                        "
                                                        :key="hrmTableCompKey"
                                                        :is="
                                                            cellComponents[key]
                                                        "
                                                        :employeeData="
                                                            row?.[key]
                                                        "
                                                        :permissionPreview="
                                                            true
                                                        "
                                                        :routeName="routeName"
                                                        :rowData="row"
                                                        :colTitle="row?.[key]"
                                                        :colData="row?.[key]"
                                                        :pageName="pageName"
                                                        :tableKey="key"
                                                        :statusPermission="
                                                            statusPermission
                                                        "
                                                        :slugs="slugs"
                                                        :statusUpdateApi="
                                                            statusUpdateApi
                                                        "
                                                        :viewTypeEmployeeComp="
                                                            viewTypeEmployeeComp
                                                        "
                                                        :isVisibleEmployees="
                                                            isVisibleEmployees
                                                        "
                                                        :employees="employees"
                                                        :locationId="locationId"
                                                        :departmentId="
                                                            departmentId
                                                        "
                                                        :assetConditions="
                                                            assetConditions
                                                        "
                                                        :assetAvailabilities="
                                                            assetAvailabilities
                                                        "
                                                        @refreshTable="
                                                            refreshTable
                                                        "
                                                        @openAdd="openAdd"
                                                        @openReasonAdd="
                                                            openReasonAdd
                                                        "
                                                        @openUpdateAttendance="
                                                            openUpdateAttendance
                                                        "
                                                        @openReportAlert="
                                                            openReportAlert
                                                        "
                                                        @openCandidateDetails="
                                                            openCandidateDetails
                                                        "
                                                        @openJobScheduleDetails="
                                                            openJobScheduleDetails
                                                        "
                                                        @submitAssetCode="
                                                            submitAssetCode
                                                        "
                                                        @updateStatus="
                                                            updateStatus
                                                        "
                                                        :statusUpdateType="
                                                            statusUpdateType
                                                        "
                                                        :class="[
                                                            getClassColumns(
                                                                selectedColumns,
                                                                availableColumns,
                                                            ),
                                                            isActiveCell(
                                                                rowIndex,
                                                                colIndex,
                                                            )
                                                                ? 'active-cell'
                                                                : '',
                                                        ]"
                                                    >
                                                    </component>
                                                </template>
                                                <template v-else>
                                                    <td
                                                        class=""
                                                        tabindex="0"
                                                        :id="`row${rowIndex}col${key}`"
                                                        @mousedown="
                                                            handleCellClick(
                                                                rowIndex,
                                                                colIndex,
                                                            )
                                                        "
                                                        @keydown="
                                                            handleKeyNavigation(
                                                                $event,
                                                                rowIndex,
                                                                colIndex,
                                                            )
                                                        "
                                                        :class="[
                                                            columnHasContent(
                                                                row[key],
                                                            )
                                                                ? 'hrmThemeTableTd'
                                                                : 'hrmThemeTableTdFit',
                                                            getClassColumns(
                                                                selectedColumns,
                                                                availableColumns,
                                                            ),
                                                           isActiveCell(rowIndex, colIndex) &&
                                                                !(row.basic_salary == 0 && salary_flag)
                                                                ? 'active-cell'
                                                                : '',
                                                            getTdCss(key),
                                                            stickyCols &&
                                                            stickyCols.includes(
                                                                key,
                                                            )
                                                                ? 'stickyColumns z-10'
                                                                : '',
                                                        ]"
                                                    >
                                                        <template
                                                            v-if="row?.[key]"
                                                        >
                                                            <template
                                                                v-if="
                                                                    typeof row?.[
                                                                        key
                                                                    ] ===
                                                                    'object'
                                                                "
                                                            >
                                                                <slot
                                                                    name="colData"
                                                                    :colData="
                                                                        row?.[
                                                                            key
                                                                        ]
                                                                    "
                                                                    :rowData="
                                                                        row
                                                                    "
                                                                >
                                                                </slot>
                                                            </template>
                                                            <span
                                                                v-else
                                                                v-html="
                                                                    row?.[key]
                                                                "
                                                            ></span>
                                                        </template>
                                                        <template v-else>
                                                            <span>--</span>
                                                        </template>
                                                    </td>
                                                </template>
                                            </template>
                                            <td
                                                class="hrmThemeActionCol text-center"
                                                v-if="actionPermission"
                                                @click="
                                                    handleCellClick(rowIndex, 0)
                                                "
                                                :id="`row${rowIndex}colaction`"
                                                @keydown="
                                                    handleKeyNavigation(
                                                        $event,
                                                        rowIndex,
                                                        focusedCell.colIndex ||
                                                            0,
                                                    )
                                                "
                                            >
                                                <slot
                                                    name="tableActionCol"
                                                    :option="row"
                                                    :items="tableData"
                                                ></slot>
                                            </td>
                                            <slot
                                                name="actionMultiPermissionsTd"
                                                :option="row"
                                                :items="tableData"
                                            >
                                            </slot>
                                        </tr>
                                    </tbody>
                                    <tfoot
                                        v-if="
                                            isShowTableFooter ||
                                            isShowTableFooterCol
                                        "
                                        class="hrm-table-footer"
                                    >
                                        <th
                                            v-if="checkBoxPermission"
                                            class="hrmThemeCheckCol themeTableCheckbox"
                                        ></th>
                                        <slot
                                            name="tableFooter"
                                            :items="tableData"
                                            :sortedColumns="sortedColumns"
                                        >
                                            <template
                                                v-for="(
                                                    key, KeyIndex
                                                ) in sortedColumns"
                                                :key="`${key}tableHeadKey`"
                                            >
                                                <th class="hrmThemeTableTh">
                                                    {{
                                                        KeyIndex == 0
                                                            ? "Total"
                                                            : ""
                                                    }}
                                                    <span
                                                        v-if="
                                                            columnCounter &&
                                                            columnCounter[key]
                                                        "
                                                    >
                                                        {{ columnCounter[key] }}
                                                    </span>
                                                </th>
                                            </template>
                                        </slot>
                                        <th
                                            v-if="actionPermission"
                                            class="hrmThemeActionCol text-center themeActionTh"
                                        ></th>
                                    </tfoot>
                                </table>
                            </template>
                        </div>
                    </div>
                    <template v-else>
                        <div class="bg-nightColor-600 rounded-lg">
                            <template v-if="isSearching">
                                <div
                                    class="w-full flex justify-between px-5 py-3 overflow-x-auto"
                                >
                                    <div class="w-full flex flex-col gap-2">
                                        <div
                                            class="w-full"
                                            v-for="i in 5"
                                            :key="i"
                                        >
                                            <Shimmer
                                                width="100%"
                                                height="30px"
                                                radius="6px"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <NoRecordFound
                                    :is_restore="is_restore"
                                    :message="
                                        trans(
                                            'lang.messages.no_record_found_from_server',
                                        )
                                    "
                                />
                            </template>
                        </div>
                    </template>
                </template>
            </template>
            <div
                id="hrmTableFooter"
                class="hrmtable_footer_bottom rounded-b-xl"
            >
                <div class="px-5 py-5">
                    <div
                        v-if="isDataLoading"
                        class="w-full flex justify-between"
                    >
                        <div class="w-full flex justify-between gap-3">
                            <div class="flex items-center gap-2">
                                <Shimmer
                                    width="120px"
                                    height="40px"
                                    radius="6px"
                                />
                                <Shimmer
                                    width="180px"
                                    height="24px"
                                    radius="6px"
                                />
                            </div>
                            <div class="flex gap-2">
                                <Shimmer
                                    v-for="i in 5"
                                    :key="i"
                                    width="30px"
                                    height="30px"
                                    radius="6px"
                                />
                            </div>
                        </div>
                    </div>
                    <FooterTable
                        v-else
                        @refreshTable="changePageNumber"
                        :pageData="pageData"
                        :pagination="pagination"
                        @pageChange="pageChange"
                    />
                </div>
            </div>
        </div>
    </div>
    <HrmModalCard
        @close="isShowExport = false"
        v-if="isShowExport"
        compName="ExportTableCols"
        :modalData="exportData"
        @refresh="exportCloseRefresh"
    />

    <!-- <ExportProgress ref="exportProgressRef" v-if="showExportProgress" /> -->
</template>
<script>
import Link from "@/Components/Common/Link.vue";
import ButtonCard from "@/Components/Common/ButtonCard.vue";
import { Icon } from "@iconify/vue";
import LoadingIcon from "@/Common/LoadingIcon.vue";

import DynamicTable from "@/Common/DynamicTable.vue";
import NoRecordFound from "@/Common/NorecordFound.vue";
import VueToggle from "vue-toggle-component";
import Shimmer from "@/Common/Shimmer.vue";
import { useAppStore } from "@/store/index";
import ToggleTooltip from "./Setting/ToggleTooltip.vue";
import _ from "lodash";
import { computed } from "vue";
import axios from "axios";

export default {
    name: "HrmTableMain",
    components: {
        Icon,
        LoadingIcon,
        ButtonCard,
        Link,
        DynamicTable,
        NoRecordFound,
        VueToggle,
        Shimmer,
        ToggleTooltip,
        // ExportProgress
    },
    props: {
        apiEndpoint: {
            type: String,
            required: false,
        },
        tableTitle: {
            type: String,
            default: "",
        },
        subTableTitle: {
            type: String,
            default: "",
        },
        deleteTitle: {
            type: String,
            default: "",
        },
        is_restore: {
            type: Boolean,
            default: false,
        },
        filterData: {
            type: Object,
        },
        items: {
            type: Array,
        },
        isAddButton: {
            type: Boolean,
        },
        queryData: {
            type: Object,
        },
        checkBoxPermission: {
            type: Boolean,
        },
        actionPermission: {
            type: Boolean,
        },
        listingPermission: {
            type: Boolean,
        },
        salary_flag:{
            type:Boolean,
            default: false
        },
        cellComponents: {
            type: Object,
        },
        routeName: {
            type: String,
        },
        pageName: {
            type: String,
        },
        statusPermission: {
            type: String,
        },
        statusUpdateApi: {
            type: String,
        },
        slugs: {
            type: Array,
        },
        isVisibleEmployees: {
            type: Array,
        },
        allMonthDate: {
            type: Array,
        },
        headComponents: {
            type: Object,
        },
        locationId: {
            type: String,
        },
        departmentId: {
            type: String,
        },
        viewTypeEmployeeComp: {
            type: String,
        },
        activeRowId: {
            type: String,
        },
        activeRowVar: {
            type: String,
        },
        employees: {
            type: Array,
        },
        assetConditions: {
            type: Array,
        },
        assetAvailabilities: {
            type: Array,
        },
        loadingData: {
            type: Object,
        },
        hrmTableData: {
            type: Object,
        },
        statusUpdateType: {
            type: String,
        },
        updatedData: {
            type: Array,
        },
        isShowSearch: {
            type: Boolean,
            default: true,
        },
        showExportButton: {
            type: Boolean,
            default: true,
        },
        isShowTableFooterCol: {
            type: Boolean,
            default: false,
        },
        stickyCols: {
            type: Array,
            default: [],
        },
    },
    data() {
        return {
            searchTimeout: null,
            searchValue: "",
            availableColumns: [],
            selectedColumns: [],
            mainCols: [],
            tableData: [],
            tableFullData: [],
            isShowSelector: true,
            pagination: {
                currentPage: 1,
                lastPage: 1,
                total: 0,
                perPage: 10,
            },
            pageData: {},
            selectedForm: [],
            selectAll: false,
            isLoaded: false,
            isDataLoading: false,
            tableButtons: {},
            tableHeadInfo: {},
            hrmTableCompKey: 1,
            updatedColSelect: 1,
            isAddingNew: false,
            isSearching: false,
            isShowTableFooter: false,
            columnCounter: {},
            focusedCell: {},

            // Export Work
            isShowExport: false,
            exportData: {
                query: this.filterData,
            },
            tableHeight: "100%",
            rowTimeout: null,
            abortController: null,
            debouncedFetchData: null,
        };
    },
    watch: {
        searchValue: function (value) {
            let self = this;
            clearTimeout(self.searchTimeout);
            self.searchTimeout = setTimeout(function () {
                self.queryData.search = value;
                self.fetchTableData(1, "searching");
            }, 700);
        },

        filterData: {
            handler: "debouncedFetchData",
            deep: true,
        },
    },
    provide() {
        return {
            availableColumns: computed(() => this.availableColumns),
            selectedColumns: computed(() => this.selectedColumns),
            mainCols: computed(() => this.mainCols),
        };
    },
    created() {
        this.isDataLoading = true;
        this.debouncedFetchData = _.debounce(this.fetchTableData, 300);
    },
    methods: {
        // Table Export Work
        isMobile() {
            window.innerWidth < 768;
        },
        openTableExport() {
            this.isShowExport = true;
            this.exportData.title = this.trans("lang.labels.export_table_data");
            this.exportData.buttonText = this.trans("lang.labels.export_table");
            this.exportData.query = this.queryData;
        },
        exportCloseRefresh(exportKey) {
            const appStore = useAppStore();
            this.isShowExport = false;
            appStore.handleExportData(exportKey);
        },
        onCellFocus(row, key) {
            this.focusedCell = {
                rowIndex: row,
                colIndex: key,
            };
        },
        handleCellClick(row, key) {
            this.focusedCell = {
                rowIndex: row,
                colIndex: key,
            };
        },
        isActiveCell(row, key) {
            return (
                this.focusedCell.rowIndex === row &&
                this.focusedCell.colIndex === key
            );
        },
        handleKeyNavigation(e) {
            let { rowIndex, colIndex } = this.focusedCell;
            const maxRows = this.tableData.length - 1;
            const maxCols = this.sortedColumns.length - 1;

            if (e.key === "ArrowRight")
                colIndex = Math.min(colIndex + 1, maxCols);
            if (e.key === "ArrowLeft") colIndex = Math.max(colIndex - 1, 0);
            if (e.key === "ArrowDown")
                rowIndex = Math.min(rowIndex + 1, maxRows);
            if (e.key === "ArrowUp") rowIndex = Math.max(rowIndex - 1, 0);

            this.focusedCell = {
                rowIndex: rowIndex,
                colIndex: colIndex,
            };

            this.$nextTick(() => {
                const colName = this.sortedColumns[colIndex];
                const cellEl = document.getElementById(
                    `row${rowIndex}col${colName}`,
                );
                if (cellEl) {
                    if (cellEl?.scrollIntoView) {
                        cellEl.scrollIntoView({
                            behavior: "smooth",
                            block: "nearest",
                            inline: "center",
                        });
                    }
                }
            });
        },
        getClassColumns(selected) {
            if (!selected || !this.mainCols) return "";
            const selectedCols = [...selected].length;
            return selectedCols > this.mainCols.length
                ? "hrmTableSelectedColAll"
                : "";
        },

        getTdCss(key) {
            return this.availableColumns?.[key]?.tdCss || "";
        },
        setPagination(pageData) {
            this.pagination.total = pageData?.total;
            this.pagination.lastPage = pageData?.last_page;
            this.pagination.currentPage = pageData?.current_page;
        },
        selectAllCheckboxes() {
            this.$emit("selectAll", this.selectAll, this.tableData);
        },
        toggleSelectAll() {
            this.selectAll = false;
        },
        refreshTable(page) {
            this.$emit("refreshTable", page);
            this.fetchTableData(page);
        },
        searchData() {
            this.$emit("setSearch", this.searchValue);
        },
        pageChange(event) {
            this.$emit("pageChange", event);
            this.queryData.perPage = event.target.value;
            this.fetchTableData(1, "searching");
        },
        openFilter() {
            this.$emit("openFilter");
        },
        updateStatus(row) {
            this.$emit("updateStatus", row);
        },
        changePageNumber(page) {
            const store = useAppStore();
            store.paginateData.currentPage = page;
            this.fetchTableData(page, "changePage");
        },
        columnHasContent(value) {
            if (!value) return false;
            if (typeof value == "string" || typeof value == "number") {
                return (
                    value != null && value !== "" && String(value).trim() !== ""
                );
            } else if (typeof value == "object") {
                return value && Object.keys(value).length > 0 ? true : false;
            } else if (Array.isArray(value) && value.length > 0) {
                return true;
            }
        },
        async fetchTableData(page, type) {
            const store = useAppStore();
            this.tableData = [];
            let currentPage = store.paginateData.currentPage || 1;
            if (type == "reload") {
                this.isShowSelector = false;
                this.isDataLoading = true;
            }
            if (type == "addNew") {
                this.isAddingNew = true;
            }
            if (type == "refreshing" || type == "searching") {
                this.isSearching = true;
                currentPage = 1;
            }
            if (type == "changePage") {
                this.isSearching = true;
            }
            this.queryData.page = currentPage;
            this.queryData.isRestore = this.is_restore ? "true" : "";
            try {
                const response = await axios.get(this.apiEndpoint, {
                    params: this.queryData,
                });
                const tableData = response.data.data;

                if (this.tableData.length == 0) {
                        this.availableColumns =
                            response.data?.columns ??
                            Object.keys(this.tableData[0]);

                        const MAIN_COLUMN = true;
                        this.selectedColumns = Object.keys(
                            this.availableColumns,
                        ).filter(
                            (key) =>
                                this.availableColumns[key].main === MAIN_COLUMN,
                        );
                        this.mainCols = [...this.selectedColumns];
                }
                this.tableData = response.data.data;
                // False after updating new entry in table
                this.isAddingNew = false;

                if (this.hrmTableData) {
                    this.hrmTableData.items = this.tableData;
                }

                this.isShowSelector = true;
                this.pageData = response.data.meta;
                this.setPagination(this.pageData);
                this.isLoaded = true;
                this.tableButtons = response.data?.buttons;
                this.tableHeadInfo = response.data?.counters;
                this.hrmTableCompKey += 1;
                this.updatedColSelect += 1;
                if (this.loadingData) {
                    this.loadingData.isReloadAlready = true;
                }
                // Handle Calculation of columns
                this.calculateColumns(this.availableColumns, this.tableData);
                this.$emit("responseMetaData", this.pageData);
                this.$emit("responseData", response.data);
                this.$emit("refreshTable");

                // Setting Store Pagi
                store.paginateData.currentPage = this.queryData.page;
            } catch (error) {
                console.error("Error fetching table data:", error);
            } finally {
                this.isDataLoading = false;
                this.isSearching = false;
            }
        },
        calculateColumns(columns, tableData) {
            const columnCounter = {};
            const totalColumns = [];

            Object.keys(columns).forEach((key) => {
                if (columns[key]?.total) {
                    totalColumns.push(key);
                }
            });

            this.isShowTableFooter = totalColumns.length > 0;

            if (this.isShowTableFooter) {
                totalColumns.forEach((col) => {
                    columnCounter[col] = tableData.reduce((sum, row) => {
                        const value = Number(row[col]);
                        return sum + (isNaN(value) ? 0 : value);
                    }, 0);
                });
                this.columnCounter = columnCounter;
            }
        },
        getLabelName(option) {
            let transvalue = "lang." + option.name.name;
            const name = this.trans(transvalue);
            return name;
        },
        updateSelections(values) {
            this.selectedColumns = values;
        },
        openAdd(data) {
            this.$emit("openAdd", data);
        },
        openReasonAdd(data) {
            this.$emit("openReasonAdd", data);
        },
        openUpdateAttendance(data) {
            this.$emit("openUpdateAttendance", data);
        },
        openReportAlert() {
            this.$emit("openReportAlert");
        },
        openJobScheduleDetails(scheduleId) {
            this.$emit("openJobScheduleDetails", scheduleId);
        },
        openCandidateDetails(candidateId) {
            this.$emit("openCandidateDetails", candidateId);
        },
        isActiveRow(rowData) {
            if (this.activeRowId && this.activeRowVar) {
                if (this.activeRowId == rowData?.[this.activeRowVar]) {
                    clearTimeout(this.rowTimeout);
                    this.rowTimeout = setTimeout(() => {
                        this.$emit("removeActiveRow");
                    }, 3500);
                    return "activeTableRow";
                }
            }
            return "";
        },
        submitAssetCode(assetCode) {
            this.$emit("submitAssetCode", assetCode);
        },
        updateTableData(data) {
            this.tableData = data;
        },
        updateHeight() {
            this.$nextTick(() => {
                if (this.$refs.tableContainer) {
                    const mainHeight = window.innerHeight;
                    const pageHeaderHeight =
                        document.getElementById("pageHeader")?.offsetHeight ||
                        0;
                    const hrmTableHeaderHeight =
                        document.getElementById("hrmTableHeader")
                            ?.offsetHeight || 0;
                    const hrmTableFooterHeight =
                        document.getElementById("hrmTableFooter")
                            ?.offsetHeight || 0;
                    const extraSpacing = 32 + 40 + 24;
                    const finalHeight =
                        mainHeight -
                        (pageHeaderHeight +
                            hrmTableHeaderHeight +
                            hrmTableFooterHeight +
                            extraSpacing);
                    this.$refs.tableContainer.style.maxHeight =
                        finalHeight + "px";
                    this.tableHeight = finalHeight + "px";
                }
            });
        },
    },
    async mounted() {
        if (this.apiEndpoint) {
            const store = useAppStore();
            store.paginateData.currentPage = 1;
            await this.fetchTableData(1, "reload");
            this.updateHeight();
        }
        window.addEventListener("resize", this.updateHeight);
    },
    unmounted() {
        clearTimeout(this.searchTimeout);
        const store = useAppStore();
        store.paginateData.currentPage = 1;
        window.removeEventListener("resize", this.updateHeight);
        clearTimeout(this.rowTimeout);
    },
    computed: {
        sortedColumns() {
            return this.selectedColumns.slice().sort((a, b) => {
                const orderA = this.availableColumns?.[a]?.display_order || 0;
                const orderB = this.availableColumns?.[b]?.display_order || 0;
                return orderA - orderB;
            });
        },
    },
};
</script>
<style>
/*breadcrumb design*/
.themeBreadcrumb .themeBreadcrumbItem {
    padding: 2px 16px;
    position: relative;
}

.themeBreadcrumb .themeBreadcrumbItem:first-child {
    padding-left: 0;
}

.themeBreadcrumb .themeBreadcrumbItem:first-child:before {
    content: "";
}

.themeBreadcrumb .themeBreadcrumbItem:before {
    content: "-";
    position: absolute;
    left: -2px;
}

/* Table design */
.hrm_Table {
    display: table;
    min-width: 1000px;
}

/* table header design */

.hrm_table_header_border {
    border-bottom: 1px dashed #565656;
}

/* table heading design */
.hrm_Table thead th:first-child {
    border-top-left-radius: 4px;
}

.hrm_Table thead th:last-child {
    border-top-right-radius: 4px;
}

.hrm_Table th {
    background: #272a2c;
    padding: 12px 15px;
    font-size: 13px;
    color: #9caab8;
    text-align: left;
    text-transform: capitalize;
    /* width: 500px !important; */
}

.hrm_Table .hrm_table_action_menu_column {
    text-align: center;
}

/* table Body design  */
.hrm_Table tbody tr:last-child td:first-child {
    border-bottom-left-radius: 4px;
}

.hrm_Table tbody tr:last-child td:last-child {
    border-bottom-right-radius: 4px;
}

.hrm_Table tbody tr:last-child {
    border-bottom: none;
}

.hrm_Table tbody tr {
    border-bottom: 1px solid #374151;
}

/* .hrm_Table tr td {
    background-color: #1f2123;
} */

.hrm_Table tbody tr td {
    font-size: 14px;
    color: #9ca4ac;
    padding: 10px 20px;
}

.hrm_Table .table_title {
    font-size: 14px;
    line-height: 2;
    color: #ffffff;
}

.hrm_Table .table_subtitle {
    font-size: 14px;
    line-height: 1;
    color: #9ca4ac;
}

/* table footer design */
.hrmtable_footer_bottom {
    border-top: 1px dashed #565656;
}

.dark .hrmThemeTableTh,
.dark .hrmThemeTableTd,
.dark .hrmThemeTableTdFit {
    /* border-right: 1px solid #222529; */
}

.hrmThemeTableTh,
.hrmThemeTableTd {
    transition:
        width 1s ease,
        min-width 1s ease,
        max-width 1s ease;
    will-change: width, min-width, max-width;
}

.hrmThemeTableTh.hrmTableSelectedColAll,
.hrmThemeTableTd.hrmTableSelectedColAll {
    width: 220px !important;
    min-width: 220px !important;
    max-width: 600px !important;
}

.hrmThemeTableTh {
    width: 120px !important;
    min-width: 120px !important;
    max-width: 120px !important;
    text-overflow: ellipsis;
    white-space: wrap;
    word-break: break-word;
}

.hrmThemeTableTd {
    width: 120px;
    min-width: 120px !important;
    max-width: 120px !important;
    text-overflow: ellipsis;
    white-space: wrap;
    word-break: break-word;
}

.hrmThemeTableTdFit {
    width: "fit-content";
    min-width: "fit-content";
    max-width: "fit-content";
}

.hrmThemeCheckCol {
    position: sticky;
    left: 0;
}

.hrmThemeActionCol {
    /* position: -webkit-sticky;
    position: sticky;
    right: 0;
    z-index: 1; */
    min-width: 120px;
    width: 120px !important;
    max-width: 120px !important;
    /* text-align: center !important; */
    /* background-color: #1f2123; */
}

.hrmTheme_table {
    display: table;
    min-width: 100%;
    table-layout: auto;
    width: auto;
}

.hrmTheme_table tr td.hrmThemeActionCol {
    background-color: transparent !important;
}

.hrmTheme_table {
    background-color: #1f2123 !important;
}

.activeTableRow {
    border: 1px solid var(--hrm_Theme_Bg_Color) !important;
}

.hrm-table-footer {
    position: sticky;
    bottom: 0;
    border-top: 1px solid #344048;
    z-index: 30;
}

.hrm-table-footer .hrmThemeTableTh {
    padding: 10px 20px !important;
}

.hrmThemeTableTdFit.active-cell,
.hrmThemeTableTd.active-cell {
    z-index: 10;
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
    background-color: #181a1c !important;
}

.hrmThemeTableTdFit.active-cell:focus,
.hrmThemeTableTd.active-cell:focus,
.hrmThemeTableTdFit:focus-visible,
.hrmThemeTableTd:focus-visible {
    outline: none !important;
    box-shadow: none !important;
    -webkit-tap-highlight-color: transparent;
}

.hrmThemeTableTh.hrmTableActiveCol {
    background-color: #2b2e30 !important;
}

.hrmTheme_table tr td {
    transition: all 0.2s !important;
}

.hrmTheme_table tr.hrmTableActiveRow td.active-cell {
    background-color: #16181a !important;
}

.hrmTheme_table tr.hrmTableActiveRow td,
.hrmTheme_table tr.hrmTableActiveRow td.hrmThemeActionCol {
    background-color: #1a1c1e !important;
}

.text-red-500 {
    --tw-text-opacity: 1;
    color: rgb(240 82 82 / var(--tw-text-opacity, 1)) !important;
}

.stickyColumns {
    position: sticky !important;
    left: 0;
}
</style>
